import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { PurchaseDocDetail } from "types/InvoiceInbox";
import {
  approvePurchaseDoc,
  blockSender,
  deletePurchaseDoc,
  dispatchPurchaseDoc,
  fetchPurchaseDoc,
  patchPurchaseDoc,
  recallPurchaseDoc,
  rejectPurchaseDoc,
  restorePurchaseDoc,
  retryExtraction,
} from "services/invoice-inbox.service";
import InvoiceDocForm from "./invoice-doc-form";
import InvoiceDocFiles, { refusalMessage } from "./invoice-doc-files";
import InvoiceDocMergeDialog from "./invoice-doc-merge-dialog";
import InvoiceDocHeader, { HeaderAction } from "./invoice-doc-header";
import InvoiceDocBanners from "./invoice-doc-banners";
import InvoiceDocSupplierCard from "./invoice-doc-supplier-card";
import InvoiceDocDetailsCard from "./invoice-doc-details-card";
import InvoiceDocAmountsCard from "./invoice-doc-amounts-card";
import InvoiceDocPaymentCard from "./invoice-doc-payment-card";
import InvoiceDocAccordions from "./invoice-doc-accordions";
import { SuggestionAccess } from "./invoice-doc-suggestion";
import { ReasonBox, buttonClass } from "./invoice-doc-ui";
import { useAppDispatch, useAppSelector } from "hooks";
import { fetchCabinetSummaryThunk } from "features/cabinet-tasks/cabinet-summary.slice";
import { usePresenceRailWidth } from "features/presence/use-presence-rail-width";

/**
 * The document panel: the file on the left, what we read out of it on the right.
 *
 * Side by side rather than on separate tabs, because the entire job here is
 * comparing the two. A reviewer who has to switch tabs to check a number stops
 * checking numbers.
 *
 * This file owns loading the document and running changes against it; what
 * is shown lives in the header, banner, card and accordion files next to it.
 */
type Props = {
  companyId: string;
  docId: string;
  onClose: () => void;
  onChanged: () => void;
};

// While a document is being read, the panel asks again this often.
const EXTRACTION_POLL_MS = 4000;

// Sent through `patchPurchaseDoc` as a number, never as the string the
// document printed — the same distinction invoice-doc-form.tsx's own
// number-typed fields make.
const SUGGESTION_MONEY_COLUMNS = new Set(["netAmount", "vatAmount", "grossAmount"]);

export default function InvoiceDocPanel({ companyId, docId, onClose, onChanged }: Props) {
  const { t, i18n } = useTranslation();
  const requestRef = useRef(0);
  // The extraction poll's own request counter — see `load`.
  const quietRef = useRef(0);
  const [detail, setDetail] = useState<PurchaseDocDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [retryingExtraction, setRetryingExtraction] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The reload after a change failed: what is on screen is the document from
  // before it. Cleared by the next load that succeeds.
  const [refreshFailed, setRefreshFailed] = useState(false);
  const [blockers, setBlockers] = useState<string[]>([]);
  const [comment, setComment] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [showReject, setShowReject] = useState(false);
  const [editing, setEditing] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  const [showDelete, setShowDelete] = useState(false);
  const [blockNotice, setBlockNotice] = useState<string | null>(null);
  const [showMerge, setShowMerge] = useState(false);
  // The supplier settings toggles need the same right the settings screen
  // itself requires — reaching the register is not enough to change how a
  // whole counterparty's future invoices get approved.
  const dispatch = useAppDispatch();
  const { role } = useAppSelector((state) => state.authReducer);
  // The logged-in user: their own pending approval slot decides whether
  // Approve / Reject are offered, and a pending Click signature of theirs gets
  // a "Sign" link.
  const currentUserId: string | null =
    useAppSelector((state) => state.authReducer?.session?.id || state.authReducer?.role?.user) || null;
  const canManageSettings = Boolean(
    role?.permissionsgroups?.includes("Master") ||
      role?.permissionsgroups?.includes("Manage invoice inbox settings")
  );
  // Keep the panel's right edge on the presence rail's left edge, exactly
  // like the bulk bar — otherwise the rail sits on top of the panel's own
  // right column (and the Close button in its header).
  const railWidth = usePresenceRailWidth(companyId);
  // Below xl the panel takes 90% of the screen, so the file beside the 380px column stays readable on a tablet.
  const panelClassName =
    "fixed inset-y-0 left-0 md:left-auto md:w-[90%] xl:w-3/4 bg-gray-50 shadow-xl z-40 transition-[right] duration-200 ease-out";

  // `quiet` is the background refresh while a document is being read: same
  // fetch, no spinner — replacing the panel with a spinner every few seconds
  // would throw away what the reviewer is looking at.
  // Resolves false only when the fetch failed and nothing newer took over, so
  // a caller that just changed the document can tell the reader that what they
  // see is out of date.
  const load = useCallback(async (quiet = false): Promise<boolean> => {
    // Quiet polls run in their own lane: taking the visible lane's slot would
    // let a background refresh invalidate the load somebody is waiting for,
    // and a quiet failure would then clear the spinner with nothing to show
    // for it (bugbot #696).
    const visibleAt = requestRef.current;
    const requestId = quiet ? ++quietRef.current : ++requestRef.current;
    // Clicking through the register faster than the network answers must not
    // let an earlier document paint over the one now on screen.
    const isSuperseded = () =>
      quiet ? requestId !== quietRef.current || visibleAt !== requestRef.current : requestId !== requestRef.current;
    try {
      if (!quiet) setLoading(true);
      const next = await fetchPurchaseDoc(companyId, docId);
      if (isSuperseded()) return true;
      setDetail(next);
      setError(null);
      setRefreshFailed(false);
      return true;
    } catch (e) {
      if (isSuperseded()) return true;
      // A blip on a refresh nobody asked for must not put a red banner over
      // the document that is still on screen.
      if (!quiet) setError(t("invoice_inbox_load_doc_failed", { defaultValue: "Could not open this document." }));
      return false;
    } finally {
      if (!quiet && requestId === requestRef.current) setLoading(false);
    }
  }, [companyId, docId, t]);

  useEffect(() => {
    load();
  }, [load]);

  // The open document is being read right now: the fields appear when the
  // worker finishes, and nothing tells the browser when that is. Poll until it
  // is no longer pending, then tell the register so the row behind the panel
  // stops saying "reading" too.
  useEffect(() => {
    if (!detail || detail.doc.extractionStatus !== "pending") return undefined;
    const intervalId = window.setInterval(() => {
      if (document.hidden) return;
      load(true);
    }, EXTRACTION_POLL_MS);
    return () => window.clearInterval(intervalId);
  }, [detail, load]);

  // Once the reading lands, the row behind the panel is the stale one. Fire on
  // the TRANSITION out of pending, not on "we are not pending now" — a
  // document that was already read when it was opened has changed nothing.
  const lastStatusRef = useRef<{ docId: string; status: string } | null>(null);
  useEffect(() => {
    if (!detail) return;
    const previous = lastStatusRef.current;
    const current = { docId: detail.doc.id, status: detail.doc.extractionStatus };
    lastStatusRef.current = current;
    if (!previous || previous.docId !== current.docId) return;
    if (previous.status === "pending" && current.status !== "pending") onChanged();
  }, [detail, onChanged]);

  // Escape closes — unless the cursor is in a field, where people expect Escape
  // to do nothing rather than throw away what they were typing.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      // Headless UI's own Dialog and Menu (the sign-request/merge dialogs and
      // the "⋯" menu) call preventDefault on the Escape they handle — closing
      // THEM. Without this check that same key also bubbles up here and
      // closes the whole panel behind it.
      if (event.defaultPrevented) return;
      const target = event.target as HTMLElement | null;
      const tag = target && target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || (target && target.isContentEditable)) return;
      if (comment.trim() || rejectReason.trim() || deleteReason.trim() || editing) return;
      onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose, comment, rejectReason, deleteReason, editing]);

  // The reload after a change that went through. Quiet, so the file preview
  // keeps its page; if it fails the document stays — the old one — with a
  // notice, because a spinner or an error here would be worse than stale.
  const reloadAfterChange = async () => {
    if (!(await load(true))) setRefreshFailed(true);
    onChanged();
  };

  const doRetryExtraction = async () => {
    if (!companyId) return;
    setRetryingExtraction(true);
    try {
      await retryExtraction(companyId, docId);
      toast.success(
        t("invoice_inbox_reextract_queued", {
          defaultValue: "Reading it again — the fields will appear here shortly.",
        })
      );
      // The panel holds its own copy of the document: reloading only the
      // register behind it would leave the unread banner and this button
      // sitting on a document that is being read again, so the toast's promise
      // never lands (bugbot #694).
      await reloadAfterChange();
    } catch (err: any) {
      const data = err && err.response && err.response.data;
      toast.error(
        (data && (data.message || data.code)) ||
          t("invoice_inbox_action_failed", { defaultValue: "The action failed." })
      );
    } finally {
      setRetryingExtraction(false);
    }
  };

  const run = async (change: () => Promise<unknown>) => {
    try {
      setBusy(true);
      setBlockers([]);
      await change();
      await reloadAfterChange();
    } catch (e: any) {
      const data = e && e.response && e.response.data;
      if (data && data.blockers) setBlockers(data.blockers);
      // A refusal is shown in the reader's language: by its code, else by the
      // specific `reason` a generic code carries (e.g. APPROVER_NOT_ELIGIBLE),
      // else the server's own text.
      setError(refusalMessage(t, (key) => (i18n as any).exists(key), e));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className={`${panelClassName} flex items-center justify-center`} style={{ right: railWidth }}>
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" aria-hidden />
        <span className="sr-only">{t("loading", { defaultValue: "Loading…" })}</span>
      </div>
    );
  }
  if (!detail) {
    return (
      <div className={`${panelClassName} p-6`} style={{ right: railWidth }}>
        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900">
          {error}
        </div>
        <button type="button" className={`${buttonClass} mt-4`} onClick={onClose}>
          {t("close", { defaultValue: "Close" })}
        </button>
      </div>
    );
  }

  const { doc, message } = detail;

  // Folding this row into another document (the other half of an email) is
  // allowed only before anybody is asked to approve it — the server refuses
  // it afterwards (SOURCE_NOT_MERGEABLE).
  const canMerge =
    !doc.deletedAt && (doc.status === "new" || doc.status === "needs_review" || doc.status === "rejected");

  // A reading that SUCCEEDED can still be wrong — an invoice number our own
  // grounding check threw away reads as a document with no number. Offering the
  // re-read only on failure meant the only way to fix that was to type it in.
  // The server decides for real; this mirrors its rule so the action is not
  // offered where it would only be refused.
  const canReRead =
    doc.extractionStatus === "ok" &&
    (doc.status === "new" || doc.status === "needs_review") &&
    !detail.events.some(
      (e) =>
        e.type === "edited" &&
        // Re-reads before the server gave them their own event type were
        // logged as edits. Counting those would hide the action after the
        // first use, on exactly the documents it exists for.
        !(e.payload && (e.payload as { action?: string }).action === "reextract")
    );

  // A deleted document, or one already being edited (its fields are
  // off-screen behind the form), gets no Edit, no in-place edit and no
  // accept button — there is nothing here for them to write into.
  const canEditDoc = !doc.deletedAt && !editing;

  // Approving and rejecting decide the CALLER's own pending slot — the server
  // refuses anybody else (NOT_YOUR_DECISION), so only its holder is offered them.
  const awaitsMyApproval =
    doc.status === "awaiting_approval" &&
    detail.approvals.some((slot) => slot.decision === "pending" && slot.approver === currentUserId);

  const suggestions: SuggestionAccess = {
    canAccept: canEditDoc,
    busy,
    onAccept: (column, suggestion) =>
      run(() =>
        patchPurchaseDoc(companyId, doc.id, {
          [column]: SUGGESTION_MONEY_COLUMNS.has(column) ? Number(suggestion) : suggestion,
        })
      ),
  };

  // The header's one main action: sending a checked document for approval,
  // or approving one that waits for the reader. Anything else has none.
  let primary: HeaderAction | null = null;
  if (!doc.deletedAt && (doc.status === "needs_review" || doc.status === "rejected")) {
    primary = {
      key: "dispatch",
      label: t("invoice_inbox_send_for_approval", { defaultValue: "Send for approval" }),
      disabled: busy,
      onClick: () => run(() => dispatchPurchaseDoc(companyId, doc.id)),
    };
  } else if (awaitsMyApproval) {
    primary = {
      key: "approve",
      label: t("approve", { defaultValue: "Approve" }),
      disabled: busy,
      onClick: () =>
        run(async () => {
          await approvePurchaseDoc(companyId, doc.id);
          dispatch(fetchCabinetSummaryThunk(companyId));
        }),
    };
  }
  const reject: HeaderAction | null = awaitsMyApproval
    ? { key: "reject", label: t("reject", { defaultValue: "Reject" }), disabled: busy, onClick: () => setShowReject(!showReject) }
    : null;

  const blockTheSender = async (fromAddress: string) => {
    const result = await blockSender(companyId, {
      pattern: fromAddress,
      scope: "address",
      reason: "blocked from the register",
    });
    // Blocking stops FUTURE mail only, and the count is worth saying out
    // loud: a rule aimed at a newsletter can easily cover the address the
    // invoices arrive on.
    setBlockNotice(
      t("invoice_inbox_blocked_notice", {
        defaultValue:
          "Future mail from {{sender}} will not become documents. {{count}} invoice(s) have come from this sender before.",
        sender: fromAddress,
        count: result.previouslyAcceptedFromSender || 0,
      })
    );
  };

  // Everything else goes behind "⋯", in this order, each only where it applies.
  const menu: HeaderAction[] = [];
  if (canEditDoc) {
    menu.push({
      key: "edit",
      label:
        doc.extractionStatus === "failed" || doc.extractionStatus === "manual"
          ? t("invoice_inbox_enter_by_hand", { defaultValue: "Enter by hand" })
          : t("edit", { defaultValue: "Edit" }),
      disabled: busy,
      onClick: () => setEditing(true),
    });
  }
  if (canReRead) {
    menu.push({
      key: "reread",
      label: t("invoice_inbox_reextract", { defaultValue: "Read it again" }),
      disabled: retryingExtraction,
      onClick: doRetryExtraction,
    });
  }
  if (canMerge) {
    menu.push({
      key: "merge",
      label: t("invoice_inbox_merge", { defaultValue: "Attach to another document" }),
      disabled: busy,
      onClick: () => setShowMerge(true),
    });
  }
  if (doc.status === "awaiting_approval") {
    menu.push({
      key: "recall",
      label: t("invoice_inbox_recall", { defaultValue: "Recall" }),
      disabled: busy,
      onClick: () => run(() => recallPurchaseDoc(companyId, doc.id)),
    });
  }
  if (message && message.fromAddress) {
    const fromAddress = message.fromAddress;
    menu.push({
      key: "block",
      label: t("invoice_inbox_block_sender", { defaultValue: "Block sender" }),
      disabled: busy,
      onClick: () => run(() => blockTheSender(fromAddress)),
    });
  }
  if (doc.deletedAt) {
    menu.push({
      key: "restore",
      label: t("restore", { defaultValue: "Restore" }),
      disabled: busy,
      onClick: () => run(() => restorePurchaseDoc(companyId, doc.id)),
    });
  } else {
    menu.push({
      key: "delete",
      label: t("delete", { defaultValue: "Remove" }),
      critical: true,
      disabled: busy,
      onClick: () => setShowDelete(!showDelete),
    });
  }

  return (
    <div className={`${panelClassName} overflow-y-auto`} style={{ right: railWidth }}>
      <div className="sticky top-0 z-20 border-b border-gray-200 bg-white">
        <InvoiceDocHeader
          docNumber={doc.docNumber}
          supplierName={(detail.supplierCustomer && detail.supplierCustomer.name) || doc.supplierNameRaw || ""}
          status={doc.status}
          primary={primary}
          reject={reject}
          menu={menu}
          onClose={onClose}
        />
      </div>

      {/* Under the bar, not in it: an open box inside the sticky header would
          keep 200px of the screen covered while the panel scrolls. */}
      {showReject && awaitsMyApproval && (
        <div className="px-4 pt-3">
          <ReasonBox
            label={t("invoice_inbox_reject_reason", { defaultValue: "Why is it being rejected?" })}
            help={t("invoice_inbox_reject_reason_help", {
              defaultValue: "The accountant sees this — without it they cannot tell what to fix.",
            })}
            value={rejectReason}
            onChange={setRejectReason}
            confirmLabel={t("invoice_inbox_confirm_reject", { defaultValue: "Confirm rejection" })}
            cancelLabel={t("cancel", { defaultValue: "Cancel" })}
            disabled={busy}
            onConfirm={() =>
              run(async () => {
                await rejectPurchaseDoc(companyId, doc.id, rejectReason);
                dispatch(fetchCabinetSummaryThunk(companyId));
                setShowReject(false);
                setRejectReason("");
              })
            }
            onCancel={() => setShowReject(false)}
          />
        </div>
      )}
      {showDelete && !doc.deletedAt && (
        <div className="px-4 pt-3">
          <ReasonBox
            label={t("invoice_inbox_delete_reason", { defaultValue: "Why is it being removed?" })}
            help={t("invoice_inbox_delete_reason_help", {
              defaultValue:
                "The document is kept and can be restored — this is what explains it to whoever looks later.",
            })}
            value={deleteReason}
            onChange={setDeleteReason}
            confirmLabel={t("invoice_inbox_confirm_delete", { defaultValue: "Confirm removal" })}
            cancelLabel={t("cancel", { defaultValue: "Cancel" })}
            disabled={busy}
            onConfirm={() =>
              run(async () => {
                await deletePurchaseDoc(companyId, doc.id, deleteReason);
                setShowDelete(false);
                setDeleteReason("");
              })
            }
            onCancel={() => setShowDelete(false)}
          />
        </div>
      )}

      <InvoiceDocBanners
        companyId={companyId}
        detail={detail}
        busy={busy}
        run={run}
        error={error}
        refreshFailed={refreshFailed}
        blockers={blockers}
        blockNotice={blockNotice}
        onDismissBlockNotice={() => setBlockNotice(null)}
        retryingExtraction={retryingExtraction}
        onRetryExtraction={doRetryExtraction}
      />

      <div className="grid grid-cols-1 gap-4 p-4 lg:grid-cols-[minmax(0,1fr)_380px]">
        {/* The document itself — every file of it, the primary one first.
            Stays mounted through every change (reloads are quiet), so the
            preview keeps the page the reader is on. On a phone it comes
            after the fields (see below). */}
        <div className="self-start md:min-h-[60dvh] overflow-hidden rounded-xl border border-gray-200 bg-white lg:sticky lg:top-16">
          <InvoiceDocFiles
            companyId={companyId}
            doc={doc}
            files={detail.files || []}
            people={detail.people || {}}
            signatures={detail.signatures || []}
            fileStates={detail.fileStates || {}}
            onChanged={reloadAfterChange}
          />
        </div>

        {/* What we read out of it. On a phone it is above the file: the
            amounts and the approval are what the reader came for. */}
        <div className="space-y-3 max-md:order-first">
          {editing ? (
            <InvoiceDocForm
              detail={detail}
              disabled={busy}
              onCancel={() => setEditing(false)}
              onSave={(changes) =>
                run(async () => {
                  await patchPurchaseDoc(companyId, doc.id, changes);
                  setEditing(false);
                })
              }
            />
          ) : (
            <>
              <InvoiceDocSupplierCard
                companyId={companyId}
                detail={detail}
                busy={busy}
                canManageSettings={canManageSettings}
                run={run}
                reloadAfterChange={reloadAfterChange}
                suggestions={suggestions}
              />
              <InvoiceDocDetailsCard
                companyId={companyId}
                doc={doc}
                busy={busy}
                canEdit={canEditDoc}
                run={run}
                suggestions={suggestions}
              />
              <InvoiceDocAmountsCard
                companyId={companyId}
                detail={detail}
                reloadAfterChange={reloadAfterChange}
                suggestions={suggestions}
              />
            </>
          )}
          {/* Outside the editing split so it stays in view while the fields
              are being corrected too. */}
          <InvoiceDocPaymentCard companyId={companyId} detail={detail} busy={busy} run={run} />
          <InvoiceDocAccordions
            companyId={companyId}
            detail={detail}
            busy={busy}
            currentUserId={currentUserId}
            run={run}
            reloadAfterChange={reloadAfterChange}
            comment={comment}
            setComment={setComment}
          />
        </div>
      </div>

      {showMerge && (
        <InvoiceDocMergeDialog
          companyId={companyId}
          doc={doc}
          onClose={() => setShowMerge(false)}
          onMerged={() => {
            // This row is gone from the register now — its files live in the
            // target — so there is nothing left to show here.
            setShowMerge(false);
            onChanged();
            onClose();
          }}
        />
      )}
    </div>
  );
}
